import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X as XIcon } from 'lucide-react'
import { useMatchMedia } from '../hooks/use-match-media'
import { BottomSheet } from '../components/primitives/bottom-sheet'
import { DiscardPrompt } from './modal'

/**
 * Animated close function provided by the nearest Drawer. Any action button
 * inside a Drawer should call this (not the parent-supplied `onClose`) so the
 * exit animation plays before the panel is removed from the tree.
 */
const DrawerCloseContext = createContext<(() => void) | null>(null)

/**
 * Returns the animated close function for the enclosing Drawer, or a no-op
 * when called outside a Drawer. Safe to use unconditionally.
 */
export function useDrawerClose(): () => void {
  return useContext(DrawerCloseContext) ?? (() => {})
}

// Keep in sync with `drawer-out-*` keyframe duration in src/index.css.
// If the two drift the drawer will either flash-remove mid-animation (our
// timeout fires early) or linger invisibly (fires late).
const CLOSE_ANIMATION_MS = 260

export type DrawerProps = {
  onClose: () => void
  children: ReactNode
  /** Optional title; renders a header with a close button. */
  title?: ReactNode
  /** Optional footer pinned to the bottom (action row). */
  foot?: ReactNode
  /** Default 460 on desktop/tablet. Ignored on mobile — always full-screen. */
  width?: number
  /** Side the drawer slides in from. Default right. */
  side?: 'left' | 'right'
  /** Hide the default close button (rare — e.g. wizard with its own exit affordance). */
  hideCloseButton?: boolean
  /**
   * Optional content rendered at the top-RIGHT of the header bar (the
   * close button moves to the LEFT). Use for primary actions (Cobrar,
   * Guardar, WhatsApp) so the user always sees them above the fold —
   * matches the Notion / Linear / Stripe pattern. Footer keeps the
   * secondary actions.
   */
  headerActions?: ReactNode
  /**
   * When true, Esc / scrim click / close-button trigger an inline
   * "Discard changes?" confirm before firing onClose. Pass
   * `formState.isDirty` from react-hook-form (or any boolean). Programmatic
   * `onClose()` calls bypass the guard.
   */
  dirty?: boolean
  /**
   * Title alignment. Default is 'left' (industry standard).
   * Pass 'center' only for simple string titles that look odd left-aligned
   * (e.g., single-word panel names without an entity header).
   */
  titleAlign?: 'left' | 'center'
}

/**
 * Desktop/tablet side-drawer implementation (≥768px). Slides in from the
 * right. Handles Escape, body-scroll lock, and focus capture.
 */
function DesktopDrawer({
  onClose,
  children,
  title,
  foot,
  width = 460,
  side = 'right',
  hideCloseButton = false,
  headerActions,
  dirty = false,
  titleAlign = 'left',
}: DrawerProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false)

  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const startClose = useCallback(() => {
    if (isClosing) return
    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      onClose()
    }, CLOSE_ANIMATION_MS)
  }, [isClosing, onClose])

  const requestClose = useCallback(() => {
    if (dirty) {
      setShowDiscardPrompt(true)
      return
    }
    startClose()
  }, [dirty, startClose])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDiscardPrompt) setShowDiscardPrompt(false)
        else requestClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [requestClose, showDiscardPrompt])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Save previously-focused element on mount + auto-focus the first focusable
  // inside the drawer so keyboard users land in the panel; restore focus to
  // the trigger on close (WCAG 2.4.3). Mirrors `<Modal>`.
  useEffect(() => {
    const previousActive = document.activeElement as HTMLElement | null
    const el = panelRef.current
    if (el) {
      const target = el.querySelector<HTMLElement>(
        '[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      target?.focus()
    }
    return () => {
      if (previousActive && document.body.contains(previousActive)) {
        previousActive.focus()
      }
    }
  }, [])

  // Portal to document.body so the drawer escapes any ancestor that
  // establishes a containing block for `position: fixed` — most notably
  // the topbar's `backdrop-filter`. Without the portal, drawers opened
  // from the topbar collapse to a thin clipped strip. Same fix applied
  // to <Modal>.
  const tree = (
    <DrawerCloseContext.Provider value={requestClose}>
      <div
        className={`drawer-scrim${isClosing ? ' drawer-scrim--closing' : ''}`}
        onClick={requestClose}
        aria-hidden="true"
      />
      <div
        className={`drawer${isClosing ? ' drawer--closing' : ''}`}
        data-side={side}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        style={{ width }}
        ref={panelRef}
        tabIndex={-1}
      >
        {title !== undefined && (
          <div className="drawer-head">
            {!hideCloseButton && (
              <button
                type="button"
                className="drawer-head-close"
                onClick={requestClose}
                aria-label="Cerrar"
              >
                <XIcon size={16} />
              </button>
            )}
            <h2
              id={titleId}
              {...(titleAlign === 'center' ? { 'data-title-align': 'center' } : {})}
            >
              {title}
            </h2>
            {headerActions && (
              <div className="drawer-head-actions" role="group" aria-label="Acciones de la reserva">
                {headerActions}
              </div>
            )}
          </div>
        )}
        <div className={`${title !== undefined ? 'drawer-body' : 'drawer-body drawer-body--full'} selectable`}>
          {children}
        </div>
        {foot && <div className="drawer-foot">{foot}</div>}
        {showDiscardPrompt && (
          <DiscardPrompt
            onCancel={() => setShowDiscardPrompt(false)}
            onDiscard={() => {
              setShowDiscardPrompt(false)
              startClose()
            }}
          />
        )}
      </div>
    </DrawerCloseContext.Provider>
  )
  if (typeof document === 'undefined') return tree
  return createPortal(tree, document.body)
}

/**
 * Mobile bottom-sheet implementation (<768px). Delegates to BottomSheet
 * primitive for slide-up, drag-dismiss, and safe-area handling.
 * Spec: docs/responsive-redesign.md §10
 */
function MobileDrawer({ onClose, children, title, foot, headerActions, dirty = false }: DrawerProps) {
  // BottomSheet accepts a plain string title for its built-in header.
  // When title is complex JSX we render it inside the body instead so
  // callers' rich title nodes (e.g. court name + status chip) are preserved.
  const sheetTitle = typeof title === 'string' ? title : undefined
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false)

  const requestClose = useCallback(() => {
    if (dirty) {
      setShowDiscardPrompt(true)
      return
    }
    onClose()
  }, [dirty, onClose])

  return (
    <DrawerCloseContext.Provider value={requestClose}>
      <BottomSheet open onClose={requestClose} {...(sheetTitle !== undefined ? { title: sheetTitle } : {})} size="full">
        {title !== undefined && typeof title !== 'string' && (
          <div className="drawer-mobile-title">{title}</div>
        )}
        {headerActions && (
          <div className="drawer-mobile-header-actions" role="group" aria-label="Acciones de la reserva">
            {headerActions}
          </div>
        )}
        {children}
        {foot && <div className="drawer-mobile-foot">{foot}</div>}
        {showDiscardPrompt && (
          <DiscardPrompt
            onCancel={() => setShowDiscardPrompt(false)}
            onDiscard={() => {
              setShowDiscardPrompt(false)
              onClose()
            }}
          />
        )}
      </BottomSheet>
    </DrawerCloseContext.Provider>
  )
}

/**
 * Side-panel primitive (drawer). Slides from the right on desktop/tablet;
 * at <768px it switches to a full-height BottomSheet automatically.
 * All callers inherit the new behavior with no prop changes.
 * Spec: docs/responsive-redesign.md §10
 */
export function Drawer(props: DrawerProps) {
  const isMobile = useMatchMedia('(max-width: 767px)')
  return isMobile ? <MobileDrawer {...props} /> : <DesktopDrawer {...props} />
}

/** Semantic alias for new code — prefer this name. */
export const SidePanel = Drawer
export type SidePanelProps = DrawerProps
